import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Lightbulb,
  Target,
  TrendingUp,
  Award,
  Users,
  Zap,
  CheckCircle,
  X,
} from 'lucide-react';
import type { FinalResults } from '../types';
import Logo from '../assests/favicon-logo.png'

interface CVOptimizationSuggestionsProps {
  results: FinalResults;
  isOpen: boolean;
  onClose: () => void;
}

const CVOptimizationSuggestions: React.FC<CVOptimizationSuggestionsProps> = ({
  results,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  

  if (!isOpen) return null;

  const generateOptimizationSuggestions = () => {
    const suggestions = [];
    const score = results.globalScore;

    // Suggestions basées sur le score global
    if (score < 70) {
      suggestions.push({
        id: 'keywords',
        icon: <Target className="w-6 h-6" />,
        title: t('cvOpt.suggestions.keywords.title'),
        priority: 'high',
        impact: t('cvOpt.suggestions.keywords.impact'),
        description: t('cvOpt.suggestions.keywords.description'),
        actions: [
          t('cvOpt.suggestions.keywords.action1'),
          t('cvOpt.suggestions.keywords.action2'),
          t('cvOpt.suggestions.keywords.action3'),
        ],
      });
    }

    // Suggestions basées sur les compétences
    if (
      results.analysisResults.skills &&
      results.analysisResults.skills.score < 80
    ) {
      suggestions.push({
        id: 'skills',
        icon: <Zap className="w-6 h-6" />,
        title: t('cvOpt.suggestions.skills.title'),
        priority: 'high',
        impact: t('cvOpt.suggestions.skills.impact'),
        description: t('cvOpt.suggestions.skills.description'),
        actions: [
          t('cvOpt.suggestions.skills.action1'),
          t('cvOpt.suggestions.skills.action2'),
          t('cvOpt.suggestions.skills.action3'),
        ],
      });
    }

    // Suggestions basées sur l'expérience
    if (
      results.analysisResults.experience &&
      results.analysisResults.experience.score < 75
    ) {
      suggestions.push({
        id: 'experience',
        icon: <TrendingUp className="w-6 h-6" />,
        title: t('cvOpt.suggestions.experience.title'),
        priority: 'medium',
        impact: t('cvOpt.suggestions.experience.impact'),
        description: t('cvOpt.suggestions.experience.description'),
        actions: [
          t('cvOpt.suggestions.experience.action1'),
          t('cvOpt.suggestions.experience.action2'),
          t('cvOpt.suggestions.experience.action3'),
        ],
      });
    }

    // Suggestions générales
    suggestions.push({
      id: 'format',
      icon: <Award className="w-6 h-6" />,
      title: t('cvOpt.suggestions.format.title'),
      priority: 'medium',
      impact: t('cvOpt.suggestions.format.impact'),
      description: t('cvOpt.suggestions.format.description'),
      actions: [
        t('cvOpt.suggestions.format.action1'),
        t('cvOpt.suggestions.format.action2'),
        t('cvOpt.suggestions.format.action3'),
        t('cvOpt.suggestions.format.action4'),
      ],
    });

    return suggestions;
  };

  const suggestions = generateOptimizationSuggestions();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between relative">
          <div className="flex items-center">
            <Lightbulb className="w-6 h-6 text-yellow-500 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                💡 {t('cvOpt.title')}
              </h2>
              <p className="text-gray-600 mt-1">{t('cvOpt.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors absolute sm:relative top-[5px] right-[5px] sm:top-[unset] sm:right-[unset]"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-blue-200 hover:shadow-md transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                  <div className="flex items-start flex-1 min-w-0">
                    <div className="text-blue-600 mr-3 sm:mr-4 mt-0.5 sm:mt-1 flex-shrink-0">
                      {suggestion.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight">
                        {suggestion.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-700 mb-3 leading-relaxed">
                        {suggestion.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col items-start sm:items-end space-x-2 sm:space-x-0 sm:space-y-2 mt-3 sm:mt-0 flex-shrink-0">
                    <span
                      className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                        suggestion.priority
                      )}`}
                    >
                      {suggestion.priority === 'high'
                        ? t('cvOpt.priority.high')
                        : suggestion.priority === 'medium'
                        ? t('cvOpt.priority.medium')
                        : t('cvOpt.priority.low')}
                    </span>
                    <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-100 px-2 py-1 rounded whitespace-nowrap">
                      {suggestion.impact}
                    </span>
                  </div>
                </div>

                <div className="ml-0 sm:ml-10">
                  <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                    {t('cvOpt.actionsTitle')}
                  </h4>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {suggestion.actions.map((action, index) => (
                      <li
                        key={index}
                        className="flex items-start text-xs sm:text-sm text-gray-700"
                      >
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="leading-relaxed">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Expert Section */}
          <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl border border-green-200">
            <div className="flex items-start">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mr-3 mt-0.5 sm:mt-1 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex gap-x-2">
                  <img src={Logo} className='max-h-7 max-w-7 min-h-7 min-w-7 rounded-full'/>
                  <h3 className="text-base sm:text-lg font-semibold text-green-900 mb-2 leading-tight">
                    {t('cvOpt.expertTitle')}
                  </h3>
                </div>
                <p className="text-green-800 text-xs sm:text-sm leading-relaxed">
                  <strong>{t('cvOpt.expertStrong')}</strong>{' '}
                  {t('cvOpt.expertText')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVOptimizationSuggestions;
